import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Tooltip from "@edaz/edazkit/Tooltip";

import { formatAmount, formatDate } from "utils/localizations";
import type PayGateToTransferRefundResponse from "../../../../entities/payGateToTransferRefundResponse";
import PayGateToTransferRefundsViewModel from "../../../../viewModels/finance/refundApprovals/payGateToTransferRefunds/payGateToTransferRefundsViewModel";

import type { ListColumnDefinition } from "../../../common/list";
import ListView from "../../../common/list";
import { ApprovalColumns } from "../../../common/approvalColumns";
import PayGateToTransferRefundsFilters from "./payGateToTransferRefundsFilters";
import { emptyValue } from "utils/emptyValue";

export const refundPaymentProcessingInfo = (translate: (code: string) => string) => (
  <Tooltip id="refund-payment-processing" trigger={["hover", "focus"]} tooltip={translate("refundPaymentProcessing")}>
    <span>
      <Icon icon={IconType.Processing} width={30} height={30} />
    </span>
  </Tooltip>
);

const PayGateToTransferRefundsView: ViewComponent<PayGateToTransferRefundsViewModel> = observer(({ vm }) => {
  const columns: ListColumnDefinition<PayGateToTransferRefundResponse>[] = [
    {
      title: <Check custom target={vm as { allItems?: boolean }} property="allItems" threeState={true} />,
      valueFormatter: props => {
        if (props.item.isSelectable) {
          return <CollectionCheck custom target={vm} property="selectedItems" disabled={false} value={props.item} />;
        }
      },
    },
    {
      valueFormatter: ({ item }) => (item.originalPayment ? "" : refundPaymentProcessingInfo(vm.translateModuleGeneral)),
    },
    {
      title: vm.translateModelAttribute("licensePlate", "licensePlate"),
      valueFormatter: ({ item }) =>
        item.charge ? `${vm.countryCode(item.charge.vehicle.countryId)} - ${item.charge.vehicle.licensePlate}` : emptyValue,
      cellClassName: "font-weight-bold",
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "authorizedAt"),
      valueFormatter: ({ item }) => (item.originalPayment ? formatDate(item.originalPayment.authorizedAt) : emptyValue),
      dateProperty: true,
    },
    {
      title: vm.translateModelAttribute("payGateRefund", "payGatePaymentIdent"),
      valueFormatter: ({ item }) => item.payGatePaymentIdent ?? emptyValue,
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "paidAmount"),
      valueFormatter: ({ item }) => (item.originalPayment ? formatAmount(item.originalPayment.paidAmount) : emptyValue),
      numberProperty: true,
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "invoiceNumber"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.invoiceNumber : emptyValue),
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "arn"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.arn : emptyValue),
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "terminalId"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.terminalId : emptyValue),
    },
    {
      title: vm.translateModelAttribute("refund", "officialRecordId"),
      valueFormatter: ({ item }) => (item.officialRecordId ? item.officialRecordId : emptyValue),
    },
    {
      title: vm.translateModelAttribute("refund", "refundPaymentTemplateId"),
      valueFormatter: ({ item }) => vm.translateRefundReason(item.refundPaymentTemplateId),
    },
    {
      title: vm.translateModelAttribute("refund", "refundAmount"),
      valueFormatter: ({ item }) => formatAmount(item.refundAmount),
      numberProperty: true,
    },
    ...ApprovalColumns<PayGateToTransferRefundResponse, PayGateToTransferRefundsViewModel>(vm),
  ];

  return (
    <>
      <PayGateToTransferRefundsFilters vm={vm} />
      <ListView columns={columns} vm={vm} showDetailWithList={true} hideTitle={true} hideButtons={true} />
    </>
  );
});

registerView(PayGateToTransferRefundsView, PayGateToTransferRefundsViewModel);
